import React, { memo, useCallback } from 'react';
// import Header from '@/components/layout/header';
// import CelloLayout from './Cello';
import { ComplimentaryTrialEndBannerHost } from '@/app-shell/complimentary-trial-end/complimentary-trial-end-banner-host';
import { IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED } from '@/app-shell/flags/evidence-flags';
import ForceUpdate from '@/app-shell/force-update/force-update';
import { IntegrationHeader } from '@/app-shell/layout/header/integration-header';
import MobileAppHeader from '@/app-shell/layout/header/mobile-app-header/mobile-app-header';
import {
  DeleteSessionModal,
  DeleteTranscriptModal,
} from '@/app-shell/layout/lazy-session-dialogs';
import { routePath } from '@/app-shell/layout/Navigation/sidebar/nav-item.utils';
import { MagicFlowDeepLinkRouter } from '@/app-shell/rpa/onboarding/magic-flow-deep-link-router';
import { isMagicFlowLinkHeldForOnboarding } from '@/app-shell/rpa/onboarding/use-route-magic-flow-deep-link';
import { SessionWorkPanelEvidenceChromeHost } from '@/composition/hosts/agents/work-chrome/session-work-panel-evidence-host';
import { WorkCreditAllowanceChromeHost } from '@/composition/hosts/agents/work-chrome/work-credit-allowance-host';
import { hasCompletedOnboardingAtom, signupSourceAtom } from '@heidi/growth/data';
import { hasIntegrationAtom } from '@heidi/integrations/ehr/data/atoms';
import { SignupSource } from '@heidi/schema/ml-scribe';
// Side-effect import — binds the app inputs the Work-home surfaces behind
// @heidi/agents read. It is here rather than in the Work route: the route is a
// Chat file, and an `@/utils` import there raises the Chat ledger's zone count.
// This layout is the route's own static import, evaluated before it renders.
import '@/composition/hosts/agents/work-home-ports-wiring';

import { getConsentLegalLinks } from '@/app-shell/app-root/legalDocuments';
import { IS_GAMIFY_TRIAL_LENGTH_ENABLED } from '@/app-shell/flags/commerce-flags';
import {
  IS_ONBOARDING_V2_TREATMENT,
  IS_POST_NOTE_QUALITY_PANEL_ENABLED,
} from '@/app-shell/flags/growth-flags';
import { HEIDI_II_MASTER_FLAG_KEY } from '@/app-shell/flags/heidi-work-umbrella';
import { useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import { useIsDictationEnabled } from '@/composition/hosts/dictation/hooks/useIsDictationEnabled';
import { clearCachedOnboardingPendingMark } from '@/features/onboarding-v2/hooks/onboardingCompletionOperations';
import { PerfDebugPanel } from '@/features/performanceTracking/perf-debug-panel';
import { useDesktopConnectivity } from '@/hooks/desktop/useDesktopConnectivity';
import { EditorVersionPrefetch } from '@/hooks/editor-version/EditorVersionPrefetch';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useIsEvidenceOnlyUser } from '@/hooks/useIsEvidenceOnlyUser';
import { useRequireRegionGuard } from '@/hooks/useRequireRegionGuard';
import { stepsMapping, tutorialVisibleAtom, useTutorial } from '@/hooks/useTutorial';
import { inAppIntegrationLoadingAtom } from '@/integrations/in-app-integration-atom';
import {
  trackGettingStartedPanelClosed,
  trackGettingStartedPanelOpenReferralModal,
  trackGettingStartedPanelOpened,
} from '@/lib/analytics/scribe/getting-started';
import { heidi2OnboardingTrackers } from '@/models/analytics/features/heidi-2-onboarding-events';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { useHeidi2BookDemoPort } from '@/utils/use-heidi-2-book-demo-port';
import { Heidi2AwarenessBanner, type Heidi2AwarenessBannerPorts } from '@heidi/agents';
import { useManualExposure } from '@heidi/analytics/events';
import { isReferralProgramAvailableForLocale } from '@heidi/growth/data';
import {
  appendAttributionParamsToRedirectUrl,
  useIsMobile,
  useVisualViewportHeight,
} from '@heidi/utils';
// Side-effect import — binds the app UI the dictation bubble renders.
import '@/composition/hosts/dictation/bubble/dictation-bubble-wiring';
import { DictationBubble } from '@heidi/dictate';
import {
  TutorialChecklist,
  useResumePendingOnboarding,
  type TutorialChecklistPorts,
} from '@heidi/growth';
import { PerfTrackingStopper, TrackingKey } from '@heidi/runtime/performance';
import { useCurrentSessionId, type InAppHref } from '@heidi/runtime/shell';
import { useCacheUserRefIdForStorage } from '@heidi/session';
import { NEW_SESSION_LIST_PATH_PREFIX } from '@heidi/session/contracts';
import { useQueryUser, useUpdateUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { ResizableLayout } from './Navigation/ResizableLayout';
import ShortcutsHelper from './ShortcutsHelper/shortcuts-helper';

interface ScribeLayoutProps {
  children: React.ReactNode;
  topBanner?: React.ReactNode;
  /** Lazy route fallbacks must not finish the workspace arrival measurement. */
  trackNavigationArrival?: boolean;
  shouldShowDesktopSidebar?: boolean;
  shouldShowMobileSidebar?: boolean;
}

const Tutorial = memo(TutorialChecklist);

/**
 * The Heidi II inputs typed in the package's own `/ui` vocabulary — the tour's
 * trackers, its consent links and its book-a-demo port — plus the session path
 * a declined tour lands on. They cross here, at the banner's only mount, rather
 * than on the headless seam, which may not name a type from that zone.
 */
const heidi2AwarenessBannerPorts: Heidi2AwarenessBannerPorts = {
  newSessionListPathPrefix: NEW_SESSION_LIST_PATH_PREFIX,
  heidi2OnboardingTrackers,
  getConsentLegalLinks,
  useHeidi2BookDemoPort,
};

function useIsTutorialVisible() {
  return useAtomValue(tutorialVisibleAtom);
}

function useTutorialAuthenticated() {
  return useIsAuthenticated().isAuthenticated;
}

// The app user module re-exports this same binding. Reading it here keeps the
// completed-at value and avoids a new legacy hub importer.
function useTutorialOnboardingCompletedAt() {
  const { data } = useQueryUser((data) => ({
    onboardingTaskCompletedAt: data?.onboarding_tasks_completed_at,
  }));
  return data?.onboardingTaskCompletedAt;
}

// On-prem and the three product gates that replace this panel. A thunk so
// each read happens in the panel's render, not when this module evaluates.
function isTutorialSuppressed() {
  return (
    isOnPrem ||
    IS_GAMIFY_TRIAL_LENGTH_ENABLED() ||
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() ||
    IS_POST_NOTE_QUALITY_PANEL_ENABLED()
  );
}

const tutorialChecklistPorts: TutorialChecklistPorts = {
  useTutorial,
  stepsMapping,
  useIsTutorialVisible,
  useIsAuthenticated: useTutorialAuthenticated,
  useOnboardingTaskCompletedAt: useTutorialOnboardingCompletedAt,
  isSuppressed: isTutorialSuppressed,
  trackPanelOpened: trackGettingStartedPanelOpened,
  trackPanelClosed: trackGettingStartedPanelClosed,
  referral: {
    isOnPrem,
    useIsEvidenceOnlyUser,
    isReferralProgramAvailableForLocale,
    trackOpenReferralModal: trackGettingStartedPanelOpenReferralModal,
  },
};

export const ScribeLayout = ({
  children,
  topBanner,
  trackNavigationArrival = true,
  shouldShowMobileSidebar,
  shouldShowDesktopSidebar = true,
}: ScribeLayoutProps) => {
  const isMobile = useIsMobile();
  useVisualViewportHeight({ enabled: isMobile });
  // Reroute authenticated-but-region-less users to onboarding so they can't
  // get stranded on an in-app page that never creates their user record.
  useRequireRegionGuard();
  const router = useRouter();
  const { pathname, asPath } = router;
  const authToken = useAuthToken();
  const { data: user, isSuccess: isUserLoaded } = useQueryUser();
  const hasCompletedOnboarding = useAtomValue(hasCompletedOnboardingAtom);
  const desktopConnectivity = useDesktopConnectivity();
  const areFlagsReady = usePosthogFlagsReady();
  const replaceRoute = useCallback(
    async (href: InAppHref) => {
      // `false` is a superseded navigation; report it like a failed one.
      if (!(await router.replace(href))) throw new Error('navigation cancelled');
    },
    [router]
  );
  const { mutateAsync: mutateUpdateUser } = useUpdateUser();
  const signupSourceFromHash = useAtomValue(signupSourceAtom);
  const storedSignupSource = user?.signup_source;
  const handleCannotResumeOnboarding = useCallback(() => {
    // These users skip the in-app survey, which is where signup_source was sent
    // and where the backend fires product_sign_up_successful. Always send it here;
    // the stored value comes before the default so a source another flow wrote stays.
    void (async () => {
      try {
        await mutateUpdateUser({
          onboarding_completed: true,
          // MOBILE-PARITY-DEBT(IDE-1054, GRO-4834): client-resolved signup_source default for
          // onboarding-gate signups. The iOS and Android apps never send signup_source (existing
          // gap, recorded on GRO-4834). Remove when the backend fires the event without it.
          signup_source:
            signupSourceFromHash ?? storedSignupSource ?? SignupSource.scribe,
        });
        clearCachedOnboardingPendingMark();
      } catch {
        logger.warn('Failed to clear pending onboarding mark for a flag-off user');
      }
    })();
  }, [mutateUpdateUser, storedSignupSource, signupSourceFromHash]);
  useResumePendingOnboarding({
    canResume: IS_ONBOARDING_V2_TREATMENT(),
    isResumeDecided: areFlagsReady,
    hasAuthToken: !!authToken,
    isUserLoaded,
    onboardingPendingAt: user?.onboarding_pending_at,
    hasCompletedOnboarding,
    pathname,
    isFromSharedTemplate: router.query.from === 'shared-template',
    isDesktopOffline: desktopConnectivity === 'offline',
    preserveRedirectParams: appendAttributionParamsToRedirectUrl,
    onNavigate: replaceRoute,
    onCannotResume: handleCannotResumeOnboarding,
  });
  useCacheUserRefIdForStorage();
  const sessionId = useCurrentSessionId();

  const isHeidiIIMasterEnabled = useFeatureFlagEnabled(HEIDI_II_MASTER_FLAG_KEY);
  useManualExposure({
    flagKey: HEIDI_II_MASTER_FLAG_KEY,
    isEnabled: isHeidiIIMasterEnabled,
    isVisible: true,
  });

  const hasIntegration = useAtomValue(hasIntegrationAtom);
  const isInAppIntegrationLoading = useAtomValue(inAppIntegrationLoadingAtom);
  const isSessionless = pathname.includes('scribe/chat');
  const isEvidenceDictationEnabled = isSessionless;
  const isDictationEnabled = useIsDictationEnabled();
  // Show mobile header when:
  // 1. Not on a session route (no sessionId) OR explicitly requested via shouldShowMobileSidebar
  // 2. Not loading integration
  // 3. On mobile
  const shouldShowMobileHeader =
    !isInAppIntegrationLoading && (shouldShowMobileSidebar || (!sessionId && isMobile));

  return (
    <WorkCreditAllowanceChromeHost>
      <SessionWorkPanelEvidenceChromeHost>
        <div
          // h-[var(--vvh,100dvh)] and max-h-[...] have vh fallbacks in globals.css for Edge ≤107.
          // The top padding reserves the 32px the fixed `<TauriAppHeader />` (rendered
          // once globally in `_app.tsx`) overlays the top with — without this the
          // ResizableLayout / sidebar contents sit under the title bar. The var is
          // published by `TauriTitlebarHeightVar` (0px on web / aux windows).
          className="flex h-[var(--vvh,100dvh)] max-h-[var(--vvh,100dvh)] w-dvw flex-col pt-[var(--app-titlebar-height,0px)]"
        >
          {isTauri() && <ForceUpdate />}
          {/* Only the desktop app receives Magic Flow deep links. It runs with the
          onboarding flag off too: My Templates then opens the template normally. */}
          {isTauri() && (
            <MagicFlowDeepLinkRouter
              isSignedIn={Boolean(authToken) && isUserLoaded}
              isOnboardingPending={isMagicFlowLinkHeldForOnboarding({
                onboardingPendingAt: user?.onboarding_pending_at,
                hasCompletedOnboarding,
                isResumeDecided: areFlagsReady,
                canResume: IS_ONBOARDING_V2_TREATMENT(),
              })}
            />
          )}
          {hasIntegration && <IntegrationHeader />}
          {shouldShowMobileHeader && <MobileAppHeader />}
          <ShortcutsHelper />
          <EditorVersionPrefetch />
          <Heidi2AwarenessBanner ports={heidi2AwarenessBannerPorts} />
          <ComplimentaryTrialEndBannerHost />
          {topBanner}
          <ResizableLayout
            shouldShowDesktopSidebar={shouldShowDesktopSidebar && !isMobile}
          >
            {children}
            {/* Closes the `perf.app.nav_menu_switch` flow `NavItem` opened on a
            sidebar click (ARD-1955): "arrived" = this layout committed the
            destination's children, one frame later. Every sidebar destination
            renders ScribeLayout, so one stopper covers the whole menu. Keyed on
            the query-stripped path so a same-page-file navigation (session A →
            session B) remounts it even though this layout instance survives.
            No `condition`: `finishTracking` no-ops when nothing is in flight. */}
            {trackNavigationArrival && (
              <PerfTrackingStopper
                key={routePath(asPath)}
                trackingKey={TrackingKey.APP_NAV_MENU_SWITCH}
              />
            )}
          </ResizableLayout>
          {!isMobile && <Tutorial ports={tutorialChecklistPorts} />}
          {(isDictationEnabled || isEvidenceDictationEnabled) && <DictationBubble />}
          <DeleteSessionModal />
          <DeleteTranscriptModal />
          {process.env.NEXT_PUBLIC_PERF_DEBUG === 'true' && <PerfDebugPanel />}
        </div>
      </SessionWorkPanelEvidenceChromeHost>
    </WorkCreditAllowanceChromeHost>
  );
};
